import React, { createContext, useCallback, useContext, useState } from 'react';
import './Toast.css';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);

  const showToast = useCallback((message, options = {}) => {
    const id = Date.now() + Math.random();
    const {
      type = 'info', // 'success' | 'error' | 'warning' | 'info'
      variant = 'default', // 'default' | 'delete'
      position,
      duration,
    } = options;

    const resolvedPosition =
      position ?? (variant === 'delete' ? 'center' : 'bottom'); // 'top' | 'center' | 'bottom'

    const resolvedDuration =
      duration ?? (variant === 'delete' ? 2000 : 3500);

    setToasts((prev) => [...prev, { id, message, type, variant, position: resolvedPosition }]);

    if (resolvedDuration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, resolvedDuration);
    }
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const confirm = useCallback(({ title, message, confirmText = 'OK', cancelText = 'Отмена' }) => {
    return new Promise((resolve) => {
      setConfirmState({
        open: true,
        title,
        message,
        confirmText,
        cancelText,
        resolve,
      });
    });
  }, []);

  const handleConfirm = (result) => {
    if (confirmState?.resolve) {
      confirmState.resolve(result);
    }
    setConfirmState(null);
  };

  return (
    <ToastContext.Provider value={{ showToast, confirm }}>
      {children}

      {/* Toasts */}
      <div className="toast-container toast-container-top">
        {toasts.filter((t) => t.position === 'top').map((toast) => (
          <div
            key={toast.id}
            className={`toast toast-${toast.type} toast-variant-${toast.variant}`}
            onClick={() => removeToast(toast.id)}
          >
            <span className="toast-message">{toast.message}</span>
          </div>
        ))}
      </div>

      <div className="toast-container toast-container-center">
        {toasts.filter((t) => t.position === 'center').map((toast) => (
          <div
            key={toast.id}
            className={`toast toast-${toast.type} toast-variant-${toast.variant}`}
            onClick={() => removeToast(toast.id)}
          >
            <span className="toast-message">{toast.message}</span>
          </div>
        ))}
      </div>

      <div className="toast-container toast-container-bottom">
        {toasts.filter((t) => t.position !== 'top' && t.position !== 'center').map((toast) => (
          <div
            key={toast.id}
            className={`toast toast-${toast.type} toast-variant-${toast.variant}`}
            onClick={() => removeToast(toast.id)}
          >
            <span className="toast-message">{toast.message}</span>
          </div>
        ))}
      </div>

      {/* Confirm dialog */}
      {confirmState?.open && (
        <div className="confirm-backdrop" onClick={() => handleConfirm(false)}>
          <div
            className="confirm-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {confirmState.title && <h3 className="confirm-title">{confirmState.title}</h3>}
            {confirmState.message && (
              <p className="confirm-message">{confirmState.message}</p>
            )}
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-button confirm-button-secondary"
                onClick={() => handleConfirm(false)}
              >
                {confirmState.cancelText}
              </button>
              <button
                type="button"
                className="confirm-button confirm-button-primary"
                onClick={() => handleConfirm(true)}
              >
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}


